import { useMemo, useState } from "react";
import { useApp, type RestockToAdvisorInput } from "@/store/AppStore";
import { inputClass, labelClass } from "@/components/base/fieldStyles";
import NumberInput from "@/components/base/NumberInput";
import { buildFunds } from "@/utils/accounting";
import { formatNumber } from "@/utils/format";
import ReponerModal from "@/pages/admin/asesores/components/ReponerModal";
import type { Product } from "@/types";

interface AdvisorRestockListProps {
  advisorId: string;
}

interface Row {
  productId: string;
  productName: string;
  sold: number;
  ordered: number;
  manual: number;
  manualIds: string[];
}

// Lista de reposición del sábado: junta lo que el asesor vendió, lo que pidió
// por encargo y lo que marcó manualmente como "necesito reponer". Cada fila trae
// su propio botón "Reponer" (no hay que buscarla a mano) y solo se habilita si
// el admin tiene stock disponible en su inventario.
const AdvisorRestockList = ({ advisorId }: AdvisorRestockListProps) => {
  const { data, addRestockItem, deleteRestockItem, restockToAdvisor } = useApp();
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [feedback, setFeedback] = useState("");
  const [reponer, setReponer] = useState<{ product: Product; suggested: number } | null>(null);

  const notify = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(""), 2400);
  };

  // Stock propio del admin para un producto (casa + punto de clear).
  const adminStock = (pid: string): number => {
    const inv = data.inventory.find((i) => i.productId === pid);
    if (!inv) return 0;
    return (inv.stockCasa ?? 0) + (inv.stockPuntoClear ?? 0);
  };

  const rows = useMemo(() => {
    const map = new Map<string, Row>();
    const get = (raw: string, pid: string): Row => {
      const name = raw.trim() || "—";
      const key = name.toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          productId: pid,
          productName: name,
          sold: 0,
          ordered: 0,
          manual: 0,
          manualIds: [],
        });
      }
      const row = map.get(key)!;
      if (!row.productId && pid) row.productId = pid;
      return row;
    };

    data.sales
      .filter((s) => s.advisorId === advisorId)
      .forEach((s) => {
        get(s.productName, s.productId).sold += s.qty;
      });
    data.encargos
      .filter((e) => e.advisorId === advisorId && e.status !== "cancelado")
      .forEach((e) => {
        get(e.productName, e.productId).ordered += e.qty;
      });
    data.restockItems
      .filter((r) => r.advisorId === advisorId)
      .forEach((r) => {
        const row = get(r.productName, r.productId);
        row.manual += r.qty;
        row.manualIds.push(r.id);
      });

    return Array.from(map.values()).sort(
      (a, b) => b.sold + b.ordered + b.manual - (a.sold + a.ordered + a.manual)
    );
  }, [data.sales, data.encargos, data.restockItems, advisorId]);

  // Cantidad sugerida a reponer: lo que el asesor consumió (vendió + encargó).
  const suggestedQty = (row: Row): number => Math.max(1, row.sold + row.ordered);

  const resolveProduct = (row: Row) =>
    data.products.find((p) => p.id === row.productId) ??
    data.products.find((p) => p.name.trim().toLowerCase() === row.productName.trim().toLowerCase());

  const openReponer = (row: Row) => {
    const product = resolveProduct(row);
    if (!product) {
      notify("Este producto ya no está en tu catálogo.");
      return;
    }
    setReponer({ product, suggested: suggestedQty(row) });
  };

  const handleReponer = async (input: RestockToAdvisorInput) => {
    await restockToAdvisor(input);
    setReponer(null);
    notify("Reposición registrada: mercancía asignada en consignación al asesor.");
  };

  const advisorName = (() => {
    const adv = data.distributors.find((d) => d.id === advisorId);
    return adv ? `${adv.names} ${adv.surnames}`.trim() : "Asesor";
  })();
  const reponerStock = reponer
    ? data.inventory.find((i) => i.productId === reponer.product.id)?.stockCasa ?? 0
    : 0;
  const cajaBalance = useMemo(
    () => buildFunds(data).funds.find((f) => f.key === "caja")?.balance ?? 0,
    [data]
  );

  const add = async () => {
    const product = data.products.find((p) => p.id === productId);
    const q = Number(qty) || 0;
    if (!product || q <= 0) return;
    if (adminStock(product.id) <= 0) {
      notify("Sin stock en tu inventario para reponer este producto.");
      return;
    }
    await addRestockItem({
      id: "",
      advisorId,
      productId: product.id,
      productName: product.name,
      qty: q,
      note: "",
      createdAt: new Date().toISOString(),
    });
    setProductId("");
    setQty(1);
    notify("Producto agregado a la reposición.");
  };

  const selectedStock = productId ? adminStock(productId) : 0;

  return (
    <div className="rounded-lg border border-accent-500/25 bg-background-200/30 p-3.5">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-accent-300">
          Lista de reposición (sábado)
        </h4>
        <span className="text-[10px] text-foreground-500">{rows.length} referencias</span>
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-foreground-500 py-2">
          Aún no hay movimientos: aparece lo que el asesor venda, encargue o marque manualmente.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-foreground-500 text-left">
                <th className="font-semibold py-1.5 pr-3">Producto</th>
                <th className="font-semibold py-1.5 px-2 text-right whitespace-nowrap">Stock admin</th>
                <th className="font-semibold py-1.5 px-2 text-right whitespace-nowrap">Vendió</th>
                <th className="font-semibold py-1.5 px-2 text-right whitespace-nowrap">Encargó</th>
                <th className="font-semibold py-1.5 px-2 text-right whitespace-nowrap">En lista</th>
                <th className="font-semibold py-1.5 pl-2 text-right whitespace-nowrap">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const product = resolveProduct(r);
                const stock = product ? adminStock(product.id) : 0;
                return (
                  <tr key={r.productName} className="border-t border-background-300/40">
                    <td className="py-1.5 pr-3 text-foreground-800 font-medium">{r.productName}</td>
                    <td
                      className={`py-1.5 px-2 text-right whitespace-nowrap ${
                        stock > 0 ? "text-accent-200 font-semibold" : "text-primary-200"
                      }`}
                    >
                      {formatNumber(stock)}
                    </td>
                    <td className="py-1.5 px-2 text-right text-foreground-700 whitespace-nowrap">
                      {r.sold > 0 ? `${formatNumber(r.sold)} und` : "—"}
                    </td>
                    <td className="py-1.5 px-2 text-right text-primary-200 whitespace-nowrap">
                      {r.ordered > 0 ? `${formatNumber(r.ordered)} und` : "—"}
                    </td>
                    <td className="py-1.5 px-2 text-right text-accent-200 font-semibold whitespace-nowrap">
                      {r.manual > 0 ? `${formatNumber(r.manual)} und` : "—"}
                    </td>
                    <td className="py-1.5 pl-2 text-right">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <button
                          onClick={() => openReponer(r)}
                          disabled={!product}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold whitespace-nowrap ${
                            product
                              ? "bg-primary-500 hover:bg-primary-600 text-foreground-950 cursor-pointer"
                              : "bg-background-200/60 text-foreground-500/50 cursor-not-allowed"
                          }`}
                          title={product ? "Comprar y reponer" : "Producto no está en el catálogo"}
                        >
                          <i className="ri-refresh-line" /> Reponer
                        </button>
                        {r.manualIds.length > 0 && (
                          <button
                            onClick={() => r.manualIds.forEach((id) => void deleteRestockItem(id))}
                            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                            aria-label="Quitar de reposición"
                            title="Quitar lo marcado manualmente"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {feedback && (
        <div className="mt-3 rounded-md bg-background-100/60 border border-accent-500/25 px-3 py-2 flex items-center gap-2">
          <i className="ri-information-line text-accent-300" />
          <p className="text-[11px] text-foreground-700">{feedback}</p>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-background-300/40 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <div>
          <label className={labelClass}>Marcar otro producto a reponer</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className={inputClass}
          >
            <option value="" className="bg-background-100">
              — selecciona un producto —
            </option>
            {data.products.map((p) => {
              const s = adminStock(p.id);
              return (
                <option key={p.id} value={p.id} className="bg-background-100">
                  {p.name} {s > 0 ? `· stock ${s}` : "· sin stock"}
                </option>
              );
            })}
          </select>
        </div>
        <div className="w-full sm:w-24">
          <label className={labelClass}>Cantidad</label>
          <NumberInput min={1} value={qty} onChange={setQty} className={inputClass} />
        </div>
        <button
          onClick={add}
          disabled={!productId || qty <= 0 || selectedStock <= 0}
          className="px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5"
        >
          <i className="ri-add-line" /> Reponer
        </button>
      </div>
      {productId && selectedStock <= 0 && (
        <p className="text-[10px] text-primary-200 mt-2 flex items-center gap-1.5">
          <i className="ri-alert-line" /> Este producto no tiene stock en tu inventario. No se puede
          reponer.
        </p>
      )}

      <ReponerModal
        open={reponer !== null}
        advisorId={advisorId}
        advisorName={advisorName}
        product={reponer?.product ?? null}
        suggestedQty={reponer?.suggested ?? 1}
        stockCasa={reponerStock}
        cajaBalance={cajaBalance}
        onClose={() => setReponer(null)}
        onSubmit={(input) => void handleReponer(input)}
      />
    </div>
  );
};

export default AdvisorRestockList;